import type { Breadcrumb, Event } from "@sentry/react";

/**
 * Keeps a document-signing link token out of Sentry. The public signer page is /app/sign#t=<token>
 * (src/sign/signApi.ts); SignPage strips the fragment once it has read it, but a URL captured before that
 * (the page-load transaction, a navigation breadcrumb, a Referer, a stack-frame filename, a console argument)
 * still carries it. So every string anywhere in an event or breadcrumb is scrubbed, not a list of known
 * fields. The walk is bounded and cycle-safe. It fails CLOSED: if scrubbing throws, the event or breadcrumb
 * is dropped (null) rather than sent unscrubbed.
 */
const TOKEN = /([#&]t=)[A-Za-z0-9_-]+/g;
// The same text URL-encoded (`#` -> %23, `&` -> %26, `=` -> %3D), e.g. inside a query string.
const TOKEN_ENCODED = /(%23t%3D|%26t%3D)[A-Za-z0-9_-]+/gi;
const MAX_DEPTH = 16;

export const scrubUrl = (u?: string): string | undefined =>
  u?.replace(TOKEN, "$1[redacted]").replace(TOKEN_ENCODED, "$1[redacted]");

/**
 * Scrub every string under `node` in place. Objects and arrays only; it stops descending at MAX_DEPTH.
 * Only for values Sentry owns (the normalised copy of an event), never for the app's own objects.
 */
const walk = (node: unknown, depth: number, seen: WeakSet<object>): void => {
  if (!node || typeof node !== "object" || seen.has(node as object)) return;
  seen.add(node as object);
  const rec = node as Record<string, unknown>;
  Object.keys(rec).forEach((k) => {
    const v = rec[k];
    if (typeof v === "string") {
      const s = scrubUrl(v);
      if (s !== v) rec[k] = s;
    } else if (v && typeof v === "object" && depth < MAX_DEPTH) {
      walk(v, depth + 1, seen);
    }
  });
};

const scrub = <T extends object | null>(x: T): T | null => {
  try {
    if (x) walk(x, 0, new WeakSet());
    return x;
  } catch {
    return null;
  }
};

const scrubString = (v: unknown): unknown => (typeof v === "string" ? scrubUrl(v) : v);

/**
 * Copy-on-write, never writes into a value it did not create: console breadcrumbs hold the app's own logged
 * objects by reference (data.arguments), so those are kept as the same references and only string items are
 * scrubbed. Coverage of nested values is not lost: Sentry puts a normalised copy of each breadcrumb into the
 * event, and scrubEvent walks that copy. Fails closed (null) if it throws.
 */
export const scrubBreadcrumb = <B extends Breadcrumb | null>(b: B): B | null => {
  try {
    if (!b) return b;
    const out: Breadcrumb = { ...b };
    if (typeof out.message === "string") out.message = scrubUrl(out.message);
    if (b.data && typeof b.data === "object") {
      const data: Record<string, unknown> = {};
      Object.keys(b.data).forEach((k) => {
        const v = b.data![k];
        data[k] = Array.isArray(v) ? v.map(scrubString) : scrubString(v);
      });
      out.data = data;
    }
    return out as B;
  } catch {
    return null;
  }
};

export const scrubEvent = <E extends Event>(e: E): E | null => scrub(e);
